import type { PKCEParams, ShopifyAuthTokens } from "@/types/shopifyCustomer";

const SHOP_ID = "97129627943";
const AUTHORIZE_URL = `https://shopify.com/${SHOP_ID}/auth/oauth/authorize`;
const SCOPES = "openid email";

const STORAGE_KEYS = {
  accessToken: "shopify_customer_access_token",
  refreshToken: "shopify_customer_refresh_token",
  expiresAt: "shopify_customer_expires_at",
  idToken: "shopify_customer_id_token",
  codeVerifier: "shopify_pkce_code_verifier",
  state: "shopify_pkce_state",
};

// ── PKCE Helpers ──

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("").slice(0, length);
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  return crypto.subtle.digest("SHA-256", encoder.encode(plain));
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function generatePKCEParams(): Promise<PKCEParams> {
  const codeVerifier = generateRandomString(96);
  const hashed = await sha256(codeVerifier);
  const codeChallenge = base64UrlEncode(hashed);
  const state = generateRandomString(32);
  return { codeVerifier, codeChallenge, state };
}

export function storePKCEParams(params: PKCEParams): void {
  localStorage.setItem(STORAGE_KEYS.codeVerifier, params.codeVerifier);
  localStorage.setItem(STORAGE_KEYS.state, params.state);
}

export function retrievePKCEParams(): { codeVerifier: string; state: string } | null {
  const codeVerifier = localStorage.getItem(STORAGE_KEYS.codeVerifier);
  const state = localStorage.getItem(STORAGE_KEYS.state);
  if (!codeVerifier || !state) return null;
  // Clear after retrieval
  localStorage.removeItem(STORAGE_KEYS.codeVerifier);
  localStorage.removeItem(STORAGE_KEYS.state);
  return { codeVerifier, state };
}

// ── Auth URL ──

export function getRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

export function buildAuthorizationUrl(clientId: string, codeChallenge: string, state: string): string {
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: "code",
    redirect_uri: getRedirectUri(),
    scope: SCOPES,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  return `${AUTHORIZE_URL}?${params.toString()}`;
}

// ── Token Storage ──

export function storeTokens(tokens: ShopifyAuthTokens): void {
  localStorage.setItem(STORAGE_KEYS.accessToken, tokens.access_token);
  localStorage.setItem(STORAGE_KEYS.refreshToken, tokens.refresh_token);
  const expiresAt = Date.now() + tokens.expires_in * 1000;
  localStorage.setItem(STORAGE_KEYS.expiresAt, expiresAt.toString());
  if (tokens.id_token) {
    localStorage.setItem(STORAGE_KEYS.idToken, tokens.id_token);
  }
}

export function getStoredToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.accessToken);
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.refreshToken);
}

export function clearTokens(): void {
  Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
}

export function isTokenExpiringSoon(): boolean {
  const expiresAt = localStorage.getItem(STORAGE_KEYS.expiresAt);
  if (!expiresAt) return true;
  // Expires within 5 minutes
  return Date.now() > parseInt(expiresAt, 10) - 5 * 60 * 1000;
}
